/**
 * The People tab: every member of a campaign in its fixed state. Members
 * come from approved bundles, waves and threads – there is no enroll button.
 * Presentational; the segment filter and "Show parked" are local view state.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { clsx } from "clsx";

import type { Person } from "../../api/console";
import { CLOSED_LABEL, berlinDate } from "../../lib/console";
import { MemberStatePill } from "./parts";

export function PeopleTable({ people, onOpenThread }: { people: Person[]; onOpenThread: (threadId: string) => void }) {
  const [segment, setSegment] = useState("");
  const [showParked, setShowParked] = useState(false);
  const segments = [...new Set(people.map((p) => p.segment).filter((s): s is string => !!s))].sort();
  const parked = people.filter((p) => p.state === "parked").length;
  const rows = people.filter((p) => (!segment || p.segment === segment) && (showParked || p.state !== "parked"));

  if (!people.length) {
    return (
      <div className="panel text-sm text-ink-low">
        Nobody yet. A person becomes a member when you approve a bundle or a wave that names them.
      </div>
    );
  }
  return (
    <div className="panel panel--flush overflow-x-auto">
      <div className="panel-header">
        <div className="flex items-center gap-2">
          {segments.length ? (
            <select
              className="input input-sm w-[200px]"
              aria-label="Segment"
              value={segment}
              onChange={(e) => setSegment(e.target.value)}
            >
              <option value="">All segments</option>
              {segments.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          ) : null}
          {parked ? (
            <button
              type="button"
              className={clsx("btn btn-sm", showParked ? "btn-main" : "btn-ghost")}
              onClick={() => setShowParked(!showParked)}
            >
              Show parked <span className="tnum">{parked}</span>
            </button>
          ) : null}
        </div>
        <span className="text-xs text-ink-low">members come from approved bundles – there is no enroll button</span>
      </div>
      <table className="table">
        <thead>
          <tr>
            <th>Person</th>
            <th>Segment</th>
            <th>State</th>
            <th>Since</th>
            <th>Thread</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((p) => (
            <tr key={p.contact_id}>
              <td>
                <Link to={`/contacts/${p.contact_id}`} className="link font-semibold">
                  {p.name}
                </Link>
                <div className="text-xs text-ink-low">
                  {p.company_name ?? ""}
                  {p.company_name && p.email ? " · " : ""}
                  {p.email ? <span className="font-mono">{p.email}</span> : null}
                </div>
              </td>
              <td className="font-mono text-xs">{p.segment ?? "–"}</td>
              <td>
                <MemberStatePill state={p.state} />
                {p.closed_as ? <span className="text-xs text-ink-low ml-1">{CLOSED_LABEL[p.closed_as]}</span> : null}
                {p.opportunity_stage ? <div className="text-xs text-ink-low mt-1">opp: {p.opportunity_stage}</div> : null}
              </td>
              <td className="font-mono text-xs text-ink-low">{berlinDate(p.since)}</td>
              <td>
                {p.thread_id ? (
                  <button type="button" className="link text-sm" onClick={() => onOpenThread(p.thread_id as string)}>
                    open →
                  </button>
                ) : (
                  <span className="text-ink-low">–</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
